import * as React from 'react'
import styled, { css } from 'styled-components'
import { useFilter } from 'modules/productDetail'
import ProductImage from 'theme/atoms/ProductImage'
import theme from 'theme'
import useTranslations from 'hooks/useTranslations'
import MoreOptionsArrow from 'theme/atoms/MoreOptionsArrow'

type Props = {
  onMoreClick: () => void
}

export default function ColorFilter(props: Props) {
  const filter = useFilter('color')
  const t = useTranslations<'templates-PDP'>()
  const options = filter.data.options
  if (options.length < 1) return null
  return (
    <Wrapper
      className="ColorFilter"
      data-cy-collection="ColorFilter"
      $options={options.length}
    >
      <div className="inner-filter">
        <div className="label">{t('color_label')}</div>
        {options.length > 1 && (
          <div className="color-container" data-cy-state="has-color-options">
            <div className="color-label">{filter.data.value}</div>
            <ul className="options">
              {options.slice(0, 5).map((opt) => (
                <Option
                  key={opt.label}
                  data-cy-handle="option"
                  $selected={opt.label === filter.data.value}
                  onClick={() => filter.setValue(opt.label)}
                >
                  <ProductImage img={opt.image} alt={opt.label} size="xs" />
                </Option>
              ))}
            </ul>
          </div>
        )}
        {options.length === 1 && (
          <div className="value" data-cy-state="is-single-filter">
            {filter.data.value}
          </div>
        )}
      </div>
      {options.length > 5 && (
        <MoreOptionsArrow
          /* TODO: bfsg show-more-options-label */ label="Show more options"
          data-cy-state="has-more-colors"
          data-cy-handle="more-colors"
          click={props.onMoreClick}
          lengthLabel={t('color_label_number', {
            pieces: options.length.toString()
          })}
        />
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div<{ $options: number }>`
  width: 100%;
  display: flex;
  padding: ${theme.spacing('s')} 0;
  border-top: 1px solid ${theme.colors.shade.b5};
  &:last-child {
    border-bottom: 1px solid ${theme.colors.shade.b5};
  }

  > .inner-filter {
    width: 86%;
    display: flex;
    ${(p) => p.$options > 1 && 'cursor: pointer;'}
    > .label {
      min-width: 5rem;
      ${theme.ty('rc-base')}
      margin-right: ${theme.spacing('s')};
    }
    > .value {
      flex: 1;
      color: ${theme.colors.b0};
      margin-right: ${theme.spacing('s')};
    }
    > .color-container {
      display: flex;
      flex-direction: column;
      align-items: flex-start;

      > .color-label {
        margin-bottom: ${theme.spacing('xs')};
      }
      > .options {
        display: flex;
        height: 2rem;

        position: relative;
        flex-wrap: wrap;

        > .more-colors {
          display: flex;
          border: 1px solid ${theme.colors.shade.b5};
          justify-content: center;
          border-radius: 0.313rem;
        }

        /* 
        if exactly 5 options are available, there is no need to save space for 
        the "add/show more" button, so we can show all available options
      */
        ${(p) =>
          p.$options <= 5
            ? css`
                > li:nth-child(5),
                > li:nth-child(6) {
                  display: none;
                }

                @media (min-width: 350px) {
                  > li:nth-child(5) {
                    display: flex;
                  }
                }

                @media (min-width: 410px) {
                  li:nth-child(6) {
                    display: flex;
                  }
                }
              `
            : css`
                > li:nth-child(4),
                > li:nth-child(5) {
                  display: none;
                }

                @media (min-width: 350px) {
                  > li:nth-child(4) {
                    display: flex;
                  }
                }

                @media (min-width: 410px) {
                  li:nth-child(5) {
                    display: flex;
                  }
                }
              `}
      }
    }
  }
`

const Option = styled.li<{ $selected: boolean }>`
  ${theme.rounding('s')}
  justify-content: center;
  position: relative;
  align-items: center;
  display: flex;
  width: 2rem;
  max-width: 2rem;
  height: 2rem;
  flex: 1 1 1.875rem;
  margin-bottom: 2rem;
  overflow: hidden;
  cursor: pointer;
  margin-right: ${theme.spacing('xs')};
  > img {
    width: 90%;
    height: 90%;
  }
  border: 1px solid
    ${(p) => (p.$selected ? theme.colors.b0 : theme.colors.shade.b5)};
`
